import React, { useEffect, useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';

// third-party
import ReactApexChart, { Props as ChartProps } from 'react-apexcharts';

// project import
import useConfig from 'hooks/useConfig';
import {
  MerchantCashAdvance,
  Rights,
  Transaction,
  TransactionStatus,
  TransactionType,
  useMcaTransactionsQuery,
  useNonWorkingDaysQuery
} from '../../graphql/types';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { LinearProgress } from '@mui/material';
import { getDaysArray } from '../../utils/getDaysArray';
import Decimal from 'decimal.js';
import useAuth from '../../hooks/useAuth';
import dayjs from 'utils/dayjsSetup';

let USDollar = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  style: 'currency',
  currency: 'USD'
});

// chart options
const areaChartOptions = {
  chart: {
    type: 'line',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    background: 'no-repeat top center/40% url("/building-columns-solid.svg")'
  },
  dataLabels: {
    enabled: false
  },
  stroke: {
    curve: 'straight',
    width: 2
  },
  grid: {
    strokeDashArray: 0
  }
};

const getWorkingDays = (days: Date[], nonWorkingDays: { date: any }[] = []) => {
  const nonWorkingDaySet = new Set(nonWorkingDays.map((d) => dayjs.utc(d.date).format('YYYY-MM-DD')));
  return days.filter((d) => {
    const dayOfWeek = dayjs.utc(d).day();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = nonWorkingDaySet.has(dayjs.utc(d).format('YYYY-MM-DD'));
    return !isWeekend && !isHoliday;
  });
};

// ==============================|| MCA PERFORMANCE CHART ||============================== //

interface Props {
  mca: MerchantCashAdvance;
}

const McaPerformanceChart = ({ mca }: Props) => {
  const theme = useTheme();
  const { mode } = useConfig();
  const { user: currentUser } = useAuth();
  const { primary, secondary } = theme.palette.text;
  const line = theme.palette.divider;

  const [options, setOptions] = useState<ChartProps>(areaChartOptions);

  const { data: nonWorkingDaysData } = useNonWorkingDaysQuery({
    variables: {},
    fetchPolicy: 'cache-and-network'
  });
  const nonWorkingDays = useMemo(() => nonWorkingDaysData?.nonWorkingDays || [], [nonWorkingDaysData]);

  const { loading, data } = useMcaTransactionsQuery({
    variables: { id: mca.id },
    fetchPolicy: 'cache-and-network'
  });

  const transactions: Transaction[] = ((data?.mcaTransactions || []) as Transaction[]).filter(
    (t) =>
      [TransactionStatus.Cleared, TransactionStatus.Pending].includes(t.status) &&
      [TransactionType.Debit, TransactionType.Collection, TransactionType.Refund, TransactionType.Expense].includes(t.type)
  );

  const days = useMemo<Date[]>(
    () => getWorkingDays(getDaysArray(mca.startDate, mca.endDate), nonWorkingDays),
    [mca.startDate, mca.endDate, nonWorkingDays]
  );

  const transactionsDays = useMemo<Date[]>(
    () => (transactions.length ? getWorkingDays(getDaysArray(mca.startDate, transactions[0].originationDate), nonWorkingDays) : []),
    [transactions.length]
  );
  const xaxisCategories = useMemo<string[]>(() => {
    const longest = [days, transactionsDays].sort(function (a, b) {
      return b.length - a.length;
    })[0];
    return longest.map((d) => getFormattedDateUTC(d));
  }, [transactionsDays.length]);
  const pmtAvg = useMemo(() => new Decimal(mca.payback).div(new Decimal(days.length - 1)), [days.length]);
  const pnlAmount = mca.pnlNet || 0;
  useEffect(() => {
    setOptions((prevState) => ({
      ...prevState,
      colors: ['#33A695', theme.palette.warning.main, '#33A6ff', pnlAmount > 0 ? theme.palette.success.main : theme.palette.error.main],
      xaxis: {
        categories: xaxisCategories,
        labels: {
          style: {
            colors: primary
          }
        },
        axisBorder: {
          show: true,
          color: secondary
        },
        tickPlacement: 'on',
        tickAmount: 11
      },
      stroke: {
        curve: 'straight',
        dashArray: [0, 0, 2, 5],
        width: 2
      },
      yaxis: {
        labels: {
          style: {
            colors: primary
          },
          formatter: function (value: any) {
            return USDollar.format(value);
          }
        }
      },
      legend: {
        position: 'top',
        labels: {
          colors: primary
        }
      },
      grid: {
        borderColor: secondary
      },
      tooltip: {
        theme: mode === 'dark' ? 'dark' : 'light',
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          const getLastKnownValue = (seriesData: any[], currentIndex: number) => {
            let value = seriesData[currentIndex];
            if (value != null && value !== 0) return value;
            for (let i = currentIndex - 1; i >= 0; i--) {
              if (seriesData[i] != null && seriesData[i] !== 0) {
                return seriesData[i];
              }
            }
            return 0;
          };
          const expectedValue = series[0][dataPointIndex] ?? 0;
          const actualValue = getLastKnownValue(series[1], dataPointIndex);
          const pendingValue = getLastKnownValue(series[2], dataPointIndex);
          const pnlValue = series[3] ? getLastKnownValue(series[3], dataPointIndex) : 0;
          const date = w.globals.categoryLabels[dataPointIndex];

          let tooltipHtml = '';

          tooltipHtml += `<div class='apexcharts-tooltip-title' style="display: flex; justify-content: flex-start; align-items: center; font-size:13px;">${date}</div>`;
          tooltipHtml += `<div class="apexcharts-custom-tooltip-row-bar" style="display:flex; align-items:center;  font-size:11px;">
           <span style="width: 8px; height: 8px; border-radius: 50%; background-color: #33A695; display: inline-block; margin-left: 8px;"></span>
             <span style="font-size: 12px; margin-left: 8px ">Expected:</span>
             <span style="font-size: 12px; margin-left: 8px;margin-right: 5px; "><strong>${USDollar.format(expectedValue)}</strong></span>
             </div>`;


          tooltipHtml += `<div class="apexcharts-custom-tooltip-row-bar" style="display:flex; align-items:center;  font-size:11px;">
            <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${w.config.colors[1]}; display: inline-block;margin-left: 8px;"></span>
            <span style="font-size: 12px; margin-left: 8px;">Actual:</span>
            <span style="font-size: 12px; margin-left: 8px; margin-right: 5px;"><strong>${USDollar.format(actualValue)}</strong></span>
            </div>`;

          tooltipHtml += `<div class="apexcharts-custom-tooltip-row-bar" style="display:flex; align-items:center;  font-size:11px;">
            <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${w.config.colors[2]}; display: inline-block;margin-left: 8px;"></span>
            <span style="font-size: 12px; margin-left: 8px;">Pending:</span>
            <span style="font-size: 12px; margin-left: 8px; margin-right: 5px;"><strong>${USDollar.format(pendingValue)}</strong></span>
            </div>`;

          tooltipHtml += `<div class="apexcharts-custom-tooltip-row-bar" style="display:flex; align-items:center;  font-size:11px;">
            <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${w.config.colors[3]}; display: inline-block;margin-left: 8px;"></span>
            <span style="font-size: 12px; margin-left: 8px;">P&L:</span>
            <span style="font-size: 12px; margin-left: 8px; margin-right: 5px;"><strong>${USDollar.format(pnlValue)}</strong></span>
            </div>`;
          tooltipHtml += '</div>';
          return tooltipHtml;
        }
      }
    }));
  }, [data, mode, primary, secondary, line, theme, pnlAmount, xaxisCategories]);

  const [series, setSeries] = useState<ApexAxisChartSeries>([
    {
      name: 'Expected',
      data: [0]
    },
    {
      name: 'Actual',
      data: [0]
    },
    {
      name: 'Pending',
      data: [0]
    }
  ]);

  useEffect(() => {
    let expected: number[] = [];
    let actual: number[] = [];
    let pending: number[] = [];
    let pnl: number[] = xaxisCategories.map(() => Math.abs(pnlAmount));
    let n = 0;
    days.forEach((date) => {
      expected.push(pmtAvg.mul(n).toNumber());
      n++;
    });
    if (days.length < transactionsDays.length) {
      const m = transactionsDays.length - days.length;
      const pmt = pmtAvg.mul(n - 1).toNumber();
      for (let i = 0; i < m; i++) {
        expected.push(pmt);
      }
    }

    let actualTotal = 0;
    let pendingTotal = 0;
    transactionsDays.forEach((date) => {
      const dayTransactions = transactions.filter((t) => dayjs.utc(t.effectiveDate).isSame(dayjs.utc(date), 'day'));
      let dayTotalAmount = dayTransactions.reduce((amount, t) => amount + t.amount, 0);
      const dayIsPending = dayTransactions.some((transaction) => transaction.status === TransactionStatus.Pending);
      if (!dayIsPending && dayTotalAmount > 0) {
        if (actual.length > 0 && actual[actual.length - 1] === null) {
          let lastNonNullValue = actual.reduce((acc, val) => (val !== null ? val : acc), 0);

          actual = actual.map((val) => (val !== null ? val : lastNonNullValue));
        }
        actualTotal += dayTotalAmount;
        actual.push(actualTotal);
      } else actual.push(null);

      if (dayIsPending && pendingTotal === 0) {
        pendingTotal += actualTotal + dayTotalAmount;
      } else if (pendingTotal > 0) {
        pendingTotal += dayTotalAmount;
      }

      pending.push(pendingTotal > 0 ? pendingTotal : null);
    });

    const series = [
      {
        name: 'Expected',
        data: expected
      },
      {
        name: 'Actual',
        data: actual
      },
      {
        name: 'Pending',
        data: pending
      }
    ];
    if (currentUser?.rights.includes(Rights.ViewMcaPnl)) {
      series.push({
        name: 'P&L',
        data: pnl
      });
    }
    setSeries(series);
  }, [data, pnlAmount]);

  if (loading) {
    return <LinearProgress />;
  }

  return <ReactApexChart options={options} series={series} type="line" height={350} />;
};

export default McaPerformanceChart;